import React, { useState } from 'react';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper,
    Box,
    IconButton,
    Radio,
    TextField,
    Autocomplete,
} from '@mui/material';
import { Plus, X, Edit, Check } from 'lucide-react';
import Toggle from './ui/toggle';

export interface StatusConfig {
    id: string;
    name: string;
    systemName?: string | null;
    isDefault: boolean;
    isLocked: boolean;
    customizable: boolean;
    nextStatusId?: string;
    lockedItems?: {
        companyDetails: boolean;
        lineItems: boolean;
        status: boolean;
    };
}

interface StatusConfigTableProps {
    statuses: StatusConfig[];
    statusOptions?: { label: string; value: string }[];
    disabled?: boolean;
    // Adding / removing a status follow their own Create / Delete permission;
    // they fall back to `disabled` (Update) when not given.
    addDisabled?: boolean;
    deleteDisabled?: boolean;
    onEdit?: (status: StatusConfig) => void;
    onDelete?: (statusId: string) => void;
    onAddStatus?: (config: {
        name: string;
        isDefault: boolean;
        isCompanyEditable: boolean;
        isLineItemEditable: boolean;
        isStatusEditable: boolean;
        isCustomizable: boolean;
        nextStatusId: string;
    }) => void;
    onToggleDefault?: (statusId: string) => void;
    onToggleLocked?: (statusId: string) => void;
    onToggleLockedItem?: (statusId: string, itemId: string) => void;
    onToggleCustomizable?: (statusId: string) => void;
    onNextStatusChange?: (statusId: string, nextStatus: string) => void;
}

const lockedItems = [
    { id: 'companyDetails', label: 'Company Details' },
    { id: 'lineItems', label: 'Line Items' },
    { id: 'status', label: 'Status' },
];

const StatusConfigTable: React.FC<StatusConfigTableProps> = ({
    statuses,
    statusOptions = [],
    disabled = false,
    addDisabled: addDisabledProp,
    deleteDisabled: deleteDisabledProp,
    onEdit,
    onDelete,
    onAddStatus,
    onToggleDefault,
    onToggleLocked,
    onToggleLockedItem,
    onToggleCustomizable,
    onNextStatusChange,
}) => {
    const addDisabled = addDisabledProp ?? disabled;
    const deleteDisabled = deleteDisabledProp ?? disabled;
    const [isAddingStatus, setIsAddingStatus] = useState(false);
    const [newStatusName, setNewStatusName] = useState('');
    const [editingStatusId, setEditingStatusId] = useState<string | null>(null);
    const [editingStatusName, setEditingStatusName] = useState('');
    const [newStatusDefault, setNewStatusDefault] = useState(false);
    const [newStatusLockedItems, setNewStatusLockedItems] = useState({
        companyDetails: false,
        lineItems: false,
        status: false,
    });
    const [newStatusCustomizable, setNewStatusCustomizable] = useState(true);
    const [newStatusNextStatus, setNewStatusNextStatus] = useState<string | null>(null);

    const handleAddClick = () => {
        setIsAddingStatus(true);
        setNewStatusName('');
        setNewStatusDefault(false);
        setNewStatusLockedItems({
            companyDetails: false,
            lineItems: false,
            status: false,
        });
        setNewStatusCustomizable(true);
        setNewStatusNextStatus(null);
    };

    const handleEditStatusName = (status: StatusConfig) => {
        setEditingStatusId(String(status.id));
        setEditingStatusName(status.name);
    };

    const handleSaveStatusName = (statusId: string) => {
        if (!editingStatusName.trim()) {
            setEditingStatusId(null);
            return;
        }
        onEdit?.({ ...statuses.find(s => String(s.id) === statusId)!, name: editingStatusName.trim() } as StatusConfig);
        setEditingStatusId(null);
    };

    const handleCancelEditStatusName = () => {
        setEditingStatusId(null);
        setEditingStatusName('');
    };

    const handleSaveNewStatus = () => {
        if (!newStatusName.trim()) {
            return;
        }
        // Pass all the configuration data
        onAddStatus?.({
            name: newStatusName.trim(),
            isDefault: newStatusDefault,
            isCompanyEditable: !newStatusLockedItems.companyDetails,
            isLineItemEditable: !newStatusLockedItems.lineItems,
            isStatusEditable: !newStatusLockedItems.status,
            isCustomizable: newStatusCustomizable,
            nextStatusId: newStatusNextStatus || '',
        });
        setIsAddingStatus(false);
        setNewStatusName('');
    };

    const handleCancelAdd = () => {
        setIsAddingStatus(false);
        setNewStatusName('');
    };

    return (
        <Box>
            <TableContainer component={Paper} sx={{ borderRadius: 0, boxShadow: 'none' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ borderTop: 'none !important', borderBottom: '2px solid #333', borderRight: 'none', borderLeft: 'none' }}>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12, color: 'var(--color-default-text)', py: 1, pl: 2 }}>Status</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12, color: 'var(--color-default-text)', py: 1 }}>Default</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12, color: 'var(--color-default-text)', py: 1 }}>Locked</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12, color: 'var(--color-default-text)', py: 1 }}>Customizable</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12, color: 'var(--color-default-text)', py: 1 }}>Next Status</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12, color: 'var(--color-default-text)', py: 1, pr: 2 }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {statuses.map((status) => (
                            <TableRow key={status.id} sx={{ borderBottom: '1px solid #f0f0f0', '&:last-child': { borderBottom: 'none' } }}>
                                <TableCell sx={{ fontSize: 13, color: 'var(--color-default-text)', py: 1.5, pl: 2 }}>
                                    {editingStatusId === String(status.id) ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: 120 }}>
                                            <TextField
                                                value={editingStatusName}
                                                onChange={(e) => setEditingStatusName(e.target.value)}
                                                size="small"
                                                autoFocus
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleSaveStatusName(String(status.id));
                                                    } else if (e.key === 'Escape') {
                                                        handleCancelEditStatusName();
                                                    }
                                                }}
                                                sx={{ minWidth: 100 }}
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={() => handleSaveStatusName(String(status.id))}
                                                sx={{ color: 'var(--color-primary)', padding: 0.5 }}
                                            >
                                                <Check size={14} />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={handleCancelEditStatusName}
                                                sx={{ color: '#ff5722', padding: 0.5 }}
                                            >
                                                <X size={14} />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 100 }}>
                                            <span>{status.name}</span>
                                            {!disabled && (
                                                <Edit size={14} color="var(--color-primary)" style={{ cursor: 'pointer' }} onClick={() => handleEditStatusName(status)} />
                                            )}
                                        </Box>
                                    )}
                                </TableCell>
                                <TableCell align="center" sx={{ py: 1.5 }}>
                                    <Radio
                                        checked={status.isDefault}
                                        onChange={() => onToggleDefault?.(status.id)}
                                        disabled={disabled}
                                        size="small"
                                        sx={{
                                            color: 'var(--color-text-muted)',
                                            opacity: disabled ? 0.5 : 1,
                                            '&.Mui-checked': {
                                                color: disabled ? 'var(--color-text-muted)' : 'var(--color-primary)',
                                            },
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{ py: 1, pl: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                        {lockedItems.map((item) => (
                                            <Box
                                                key={item.id}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    fontSize: 12,
                                                    color: 'var(--color-default-text)',
                                                }}
                                            >
                                                <Toggle
                                                    checked={status.lockedItems?.[item.id as keyof typeof status.lockedItems] ?? false}
                                                    onChange={() => onToggleLockedItem?.(status.id, item.id)}
                                                    disabled={disabled}
                                                    size="smaller"
                                                />
                                                <span>{item.label}</span>
                                            </Box>
                                        ))}
                                    </Box>
                                </TableCell>
                                <TableCell align="center" sx={{ py: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <Toggle
                                            checked={status.customizable || !!status.systemName}
                                            onChange={() => onToggleCustomizable?.(status.id)}
                                            disabled={disabled || !!status.systemName}
                                            size="medium"
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ py: 1.5 }}>
                                    <Autocomplete
                                        options={statusOptions.filter(opt => opt.value !== String(status.id))}
                                        value={statusOptions.find(opt => opt.value === String(status.nextStatusId)) || null}
                                        onChange={(_, newValue) => {
                                            onNextStatusChange?.(status.id, newValue?.value || '');
                                        }}
                                        getOptionLabel={(option) => option?.label || ''}
                                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                        renderInput={(params) => <TextField {...params} />}
                                        disabled={disabled}
                                        size="small"
                                        sx={{ minWidth: 140, fontSize: 13 }}
                                    />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 1.5, pr: 2 }}>
                                    {!status.systemName && (
                                        <IconButton
                                            size="small"
                                            onClick={() => onDelete?.(status.id)}
                                            disabled={deleteDisabled}
                                            sx={{ color: 'var(--color-text-muted)', padding: 0.5, opacity: deleteDisabled ? 0.4 : 1, '&:hover': deleteDisabled ? {} : { color: '#ff5722' } }}
                                        >
                                            <X size={16} />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {isAddingStatus && (
                            <TableRow sx={{ borderBottom: '1px solid #f0f0f0', backgroundColor: 'var(--color-container-background)' }}>
                                <TableCell sx={{ fontSize: 13, color: 'var(--color-default-text)', py: 1.5, pl: 2 }}>
                                    <TextField
                                        value={newStatusName}
                                        onChange={(e) => setNewStatusName(e.target.value)}
                                        placeholder="Enter status name"
                                        size="small"
                                        autoFocus
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                handleSaveNewStatus();
                                            } else if (e.key === 'Escape') {
                                                handleCancelAdd();
                                            }
                                        }}
                                        sx={{ minWidth: 100 }}
                                    />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 1.5 }}>
                                    <Radio
                                        checked={newStatusDefault}
                                        onChange={(e) => setNewStatusDefault(e.target.checked)}
                                        size="small"
                                        sx={{
                                            color: 'var(--color-text-muted)',
                                            '&.Mui-checked': {
                                                color: 'var(--color-primary)',
                                            },
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{ py: 1.5, pl: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                        {lockedItems.map((item) => (
                                            <Box
                                                key={item.id}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    fontSize: 12,
                                                    color: 'var(--color-default-text)',
                                                }}
                                            >
                                                <Toggle
                                                    checked={newStatusLockedItems[item.id as keyof typeof newStatusLockedItems]}
                                                    onChange={() =>
                                                        setNewStatusLockedItems(prev => ({
                                                            ...prev,
                                                            [item.id]: !prev[item.id as keyof typeof prev]
                                                        }))
                                                    }
                                                    size="smaller"
                                                />
                                                <span>{item.label}</span>
                                            </Box>
                                        ))}
                                    </Box>
                                </TableCell>
                                <TableCell align="center" sx={{ py: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <Toggle
                                            checked={newStatusCustomizable}
                                            onChange={() => setNewStatusCustomizable(!newStatusCustomizable)}
                                            size="medium"
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ py: 1.5 }}>
                                    <Autocomplete
                                        options={statusOptions}
                                        value={statusOptions.find(opt => opt.value === newStatusNextStatus) || null}
                                        onChange={(_, newValue) => {
                                            setNewStatusNextStatus(newValue?.value || null);
                                        }}
                                        getOptionLabel={(option) => option?.label || ''}
                                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                        renderInput={(params) => <TextField {...params} />}
                                        size="small"
                                        sx={{ minWidth: 140, fontSize: 12 }}
                                    />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 1.5, pr: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                        <IconButton size="small" onClick={handleSaveNewStatus} sx={{ color: 'var(--color-primary)' }}>
                                            <Check size={16} />
                                        </IconButton>
                                        <IconButton size="small" onClick={handleCancelAdd} sx={{ color: '#ff5722' }}>
                                            <X size={16} />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {!isAddingStatus && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, opacity: addDisabled ? 0.5 : 1 }}>
                    <Box
                        sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            backgroundColor: 'var(--color-primary)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: addDisabled ? 'default' : 'pointer',
                        }}
                        onClick={addDisabled ? undefined : handleAddClick}
                    >
                        <Plus size={16} color="white" />
                    </Box>
                    <Box
                        onClick={addDisabled ? undefined : handleAddClick}
                        sx={{
                            cursor: addDisabled ? 'default' : 'pointer',
                            color: 'var(--color-primary)',
                            fontWeight: 600,
                            fontSize: 13,
                        }}
                    >
                        Add Status
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default StatusConfigTable;
